<template>
  <div class="flex1">
    <div class="row" style="justify-content: space-between">
      <router-link 
        :to="`/${appId}/templates/new`"
        class="btn btn-sm btn-primary"
        style="margin-left: 8px"
      >
        Create New Template
      </router-link>
    </div>

    <div class="row titles">
      <div class="col-md-3 col-12" style="cursor: pointer" @click="changeSortParams('name')">
        Template Name {{ sortParams.field === "name" ? (sortParams.direction === "ASC" ? "↓" : "↑") : "" }}
      </div>
      <div class="col-md-4 col-12">Description</div>
      <div class="col-md-2 col-12" style="cursor: pointer" @click="changeSortParams('parameters')">
        Parameters {{ sortParams.field === "parameters" ? (sortParams.direction === "ASC" ? "↓" : "↑") : "" }}
      </div>
      <div class="col-md-2 col-12" style="cursor: pointer" @click="changeSortParams('update_time')">
        Last Updated {{ sortParams.field === "update_time" ? (sortParams.direction === "ASC" ? "↓" : "↑") : "" }}
      </div>
      <div class="col-md-1 col-12 text-center">Actions</div>
    </div>

    <div v-if="loading" class="text-center py-4">
      <div class="spinner"></div>
    </div>

    <div v-else-if="error" class="alert alert-danger mx-3 my-4">
      {{ error }}
    </div>

    <div v-else>
      <div 
        class="row mb-1 line-hover"
        v-for="template in sortedTemplates" 
        :key="template.id"
      >
        <div class="col-md-3 col-12 center-para">{{ template.name }}</div>
        <div class="col-md-4 col-12 center-para text-truncate">{{ template.description }}</div>
        <div class="col-md-2 col-12 center-para">
          <span class="parameter-count">{{ template.parameters.length }}</span>
        </div>
        <div class="col-md-2 col-12 center-para">
          <span :title="formatFullDate(template.update_time)" class="update-time">
            {{ formatDate(template.update_time) }}
          </span>
        </div>
        <div class="col-md-1 col-12 text-center center-para">
          <popup-menu>
            <template v-slot:trigger>
              <i class="fas fa-ellipsis-v" />
            </template>
            <template v-slot:content>
              <div class="text-left">
                <router-link :to="`/${appId}/templates/${template.id}`">
                  <i class="fas fa-pen-square" /> Edit
                </router-link>
              </div>
              <a @click="duplicateTemplate(template)">
                <i class="fas fa-copy" /> Duplicate
              </a>
              <a @click="deleteTemplate(template.id)" style="color: red">
                <i class="fas fa-ban" /> Delete
              </a>
            </template>
          </popup-menu>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { managerService } from '../services/manager.service';
export default {
  name: 'TemplateManagement',
  data() {
    return {
      error: null,
      sortParams: {
        field: 'name',
        direction: 'ASC'
      }
    };
  },
  computed: {
    appId() {
      return this.$store.getters['app/activeApp'];
    },
    templates() {
      return this.$store.getters.templates || [];
    },
    loading() {
      return this.$store.state.loading.entities.template || false;
    },
    sortedTemplates() {
      return [...this.templates].sort((a, b) => {
        const aField = a[this.sortParams.field];
        const bField = b[this.sortParams.field];
        
        if (this.sortParams.field === 'parameters') {
          return this.sortParams.direction === 'ASC' 
            ? a.parameters.length - b.parameters.length
            : b.parameters.length - a.parameters.length;
        }

        if (this.sortParams.field === 'update_time') {
          const aTime = a.update_time ? Number(a.update_time) : 0;
          const bTime = b.update_time ? Number(b.update_time) : 0;
          return this.sortParams.direction === 'ASC' ? aTime - bTime : bTime - aTime;
        }

        if (typeof aField === 'string') {
          return this.sortParams.direction === 'ASC'
            ? aField.localeCompare(bField)
            : bField.localeCompare(aField);
        }

        return this.sortParams.direction === 'ASC'
          ? aField - bField
          : bField - aField;
      });
    }
  },
  methods: {
    changeSortParams(field) {
      if (this.sortParams.field === field) {
        this.sortParams.direction = this.sortParams.direction === 'ASC' ? 'DESC' : 'ASC';
      } else {
        this.sortParams = { field, direction: 'ASC' };
      }
    },
    formatDate(timestamp) {
      if (!timestamp) return '-';
      const date = new Date(Number(timestamp));
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) {
        const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
        if (diffHours === 0) {
          const diffMinutes = Math.floor(diffTime / (1000 * 60));
          return diffMinutes === 0 ? 'Just now' : `${diffMinutes}m ago`;
        }
        return `${diffHours}h ago`;
      } else if (diffDays === 1) {
        return 'Yesterday';
      } else if (diffDays < 7) {
        return `${diffDays}d ago`;
      } else {
        return date.toLocaleDateString();
      }
    },
    formatFullDate(timestamp) {
      if (!timestamp) return '-';
      const date = new Date(Number(timestamp));
      return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    },
    async deleteTemplate(id) {
      if (confirm('Are you sure you want to delete this template?')) {
        try {
          await managerService.configDelete('template', id, { aid: this.appId });
          await this.$store.dispatch('configFetch', 'template');
        } catch (err) {
          this.error = 'Failed to delete template';
          console.error(err);
        }
      }
    },
    async duplicateTemplate(template) {
      try {
        const newTemplate = {
          ...template,
          name: `${template.name} (Copy)`,
          id: undefined
        };
        await managerService.configAdd('template', newTemplate, { aid: this.appId });
        await this.$store.dispatch('configFetch', 'template');
      } catch (err) {
        this.error = 'Failed to duplicate template';
        console.error(err);
      }
    }
  },
};
</script>

<style scoped>
.flex1 {
  padding: 20px;
  background-color: #1e1e2f;
  min-height: 100vh;
}

.titles {
  color: #ffffff;
  font-weight: 600;
  padding: 15px 0;
  margin-top: 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.line-hover {
  padding: 12px 0;
  color: #ffffff;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.line-hover:hover {
  background-color: rgba(255, 255, 255, 0.05);
}

.center-para {
  display: flex;
  align-items: center;
  padding: 0 15px;
}

.parameter-count {
  background: rgba(98, 124, 255, 0.2);
  color: #627cff;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.9em;
}

.spinner {
  width: 30px;
  height: 30px;
  border: 3px solid rgba(98, 124, 255, 0.2);
  border-top-color: #627cff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.btn-primary {
  background-color: #627cff;
  border-color: #627cff;
  color: white;
  padding: 8px 16px;
  font-size: 0.9em;
}

.btn-primary:hover {
  background-color: #4d63cc;
  border-color: #4d63cc;
}

.text-truncate {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

:deep(.popup-menu) {
  background: #2a2a45;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

:deep(.popup-menu a) {
  color: #ffffff;
  padding: 8px 16px;
  display: block;
  text-decoration: none;
}

:deep(.popup-menu a:hover) {
  background: rgba(98, 124, 255, 0.1);
}

.fas {
  margin-right: 8px;
}

.alert {
  background-color: rgba(231, 76, 60, 0.2);
  border-color: rgba(231, 76, 60, 0.3);
  color: #e74c3c;
}

.update-time {
  color: #a0a0a0;
  font-size: 0.9em;
}

.update-time:hover {
  color: #ffffff;
}
</style> 